import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { api, API, fmtMoney, formatApiError } from "../lib/api";

const TABS = [
  { id: "settings", label: "Payment settings" },
  { id: "quotes", label: "Quote requests" },
  { id: "orders", label: "Manual orders" },
  { id: "consents", label: "Legal consents" },
];

const QUOTE_STATUS_NEXT = {
  open: ["contacted", "accepted", "declined", "expired"],
  contacted: ["accepted", "declined", "expired"],
  accepted: ["expired"],
  declined: [],
  expired: [],
};

export default function AdminDesk() {
  const [tab, setTab] = useState("settings");

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 pb-6">
        <div className="label-tag mb-4">Trading desk</div>
        <h1 className="font-serif text-5xl tracking-tight">Compliance & settlement console.</h1>
        <p className="text-ink2 mt-3 max-w-2xl text-sm">
          Manage SWIFT/USDT instructions, review BD quote requests, and confirm manually-settled orders. All changes are audited.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="border-b border-line flex items-end gap-1 overflow-x-auto">
          {TABS.map((tt) => (
            <button
              key={tt.id}
              onClick={() => setTab(tt.id)}
              data-testid={`desk-tab-${tt.id}`}
              className={`px-5 py-3 text-xs font-mono uppercase tracking-[0.18em] border-t border-x border-line -mb-px whitespace-nowrap ${
                tab === tt.id ? "bg-surface text-ink border-b-surface" : "bg-bg/40 text-ink2 hover:text-ink"
              }`}
            >
              {tt.label}
            </button>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
        {tab === "settings" && <PaymentSettings />}
        {tab === "quotes" && <QuoteRequests />}
        {tab === "orders" && <ManualOrders />}
        {tab === "consents" && <LegalConsents />}
      </section>
    </Layout>
  );
}

// -------- Payment settings panel --------
function PaymentSettings() {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/settings/payment").then((r) => setF(r.data)).catch(() => toast.error("Could not load settings."));
  }, []);

  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/admin/settings/payment", f);
      setF(data);
      toast.success("Payment settings updated.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (!f) return <div className="font-mono text-sm text-ink2">Loading…</div>;
  return (
    <form onSubmit={save} className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-testid="settings-form">
      <div className="border border-line bg-surface p-6 space-y-4">
        <div className="label-tag">SWIFT bank wire</div>
        <FieldGroup>
          <Input label="Beneficiary" v={f.swift_beneficiary} onChange={(v) => set("swift_beneficiary", v)} testid="s-beneficiary" />
          <Input label="Bank name" v={f.swift_bank_name} onChange={(v) => set("swift_bank_name", v)} testid="s-bank" />
          <Input label="BIC / SWIFT code" v={f.swift_bic} onChange={(v) => set("swift_bic", v)} mono testid="s-bic" />
          <Input label="IBAN / account number" v={f.swift_iban} onChange={(v) => set("swift_iban", v)} mono testid="s-iban" />
          <Input label="Bank address" v={f.swift_bank_address} onChange={(v) => set("swift_bank_address", v)} testid="s-addr" textarea />
        </FieldGroup>
      </div>

      <div className="space-y-8">
        <div className="border border-line bg-surface p-6 space-y-4">
          <div className="label-tag">USDT (stablecoin)</div>
          <FieldGroup>
            <Input label="Network" v={f.usdt_network} onChange={(v) => set("usdt_network", v)} placeholder="ERC-20 / TRC-20 / BEP-20 / Solana" mono testid="s-usdt-network" />
            <Input label="Wallet address" v={f.usdt_address} onChange={(v) => set("usdt_address", v)} mono testid="s-usdt-address" />
          </FieldGroup>
        </div>
        <div className="border border-line bg-surface p-6 space-y-4">
          <div className="label-tag">Notifications</div>
          <FieldGroup>
            <Input label="Admin notify email" v={f.admin_notify_email} onChange={(v) => set("admin_notify_email", v)} placeholder="quotes@yourcompany.com" testid="s-notify" />
          </FieldGroup>
        </div>
        <div className="flex justify-end">
          <button type="submit" disabled={busy} className="btn-primary !px-8 !py-3 !text-xs disabled:opacity-50" data-testid="settings-save">
            {busy ? "Saving…" : "Save settings"}
          </button>
        </div>
        <p className="text-xs text-ink2 font-mono">
          Settings stored in MongoDB override .env defaults. Leaving a field empty clears the override.
        </p>
      </div>
    </form>
  );
}

function FieldGroup({ children }) { return <div className="space-y-3">{children}</div>; }
function Input({ label, v, onChange, mono, textarea, placeholder, testid }) {
  return (
    <label className="block">
      <span className="label-tag">{label}</span>
      {textarea ? (
        <textarea data-testid={testid} value={v || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={2}
          className={`mt-2 w-full bg-bg border border-line px-3 py-2 text-sm ${mono ? "font-mono" : ""} focus:outline-none focus:border-ink`} />
      ) : (
        <input data-testid={testid} value={v || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
          className={`mt-2 w-full bg-bg border border-line px-3 py-2 text-sm ${mono ? "font-mono" : ""} focus:outline-none focus:border-ink`} />
      )}
    </label>
  );
}

// -------- Quote requests panel --------
function QuoteRequests() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("");
  const [kind, setKind] = useState("single"); // "single" | "bulk"
  const [replyTo, setReplyTo] = useState(null);

  const load = () => api.get("/admin/quotes" + (filter ? `?status=${filter}` : ""))
    .then((r) => setItems(r.data.items || []));
  useEffect(() => { load();   }, [filter]);

  const visible = (items || []).filter((q) => (kind === "bulk" ? q.type === "bulk" : q.type !== "bulk"));
  const counts = (items || []).reduce((acc, q) => {
    if (q.type === "bulk") acc.bulk += 1; else acc.single += 1;
    return acc;
  }, { single: 0, bulk: 0 });

  const changeStatus = async (ref, status) => {
    try {
      await api.patch(`/admin/quotes/${ref}?status=${status}`);
      toast.success(`Quote ${ref} → ${status}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  return (
    <div data-testid="quotes-panel">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="inline-flex border border-line text-[11px] font-mono uppercase">
          <button
            type="button"
            onClick={() => setKind("single")}
            data-testid="quote-kind-single"
            className={`px-3 py-2 ${kind === "single" ? "bg-ink text-white" : "text-ink2 hover:text-ink"}`}
          >
            Single project · {counts.single}
          </button>
          <button
            type="button"
            onClick={() => setKind("bulk")}
            data-testid="quote-kind-bulk"
            className={`px-3 py-2 border-l border-line ${kind === "bulk" ? "bg-ink text-white" : "text-ink2 hover:text-ink"}`}
          >
            Bulk · {counts.bulk}
          </button>
        </div>
        <span className="label-tag ml-2">Status:</span>
        {["", "open", "contacted", "accepted", "declined", "expired"].map((s) => (
          <button key={s || "all"} onClick={() => setFilter(s)}
            className={`px-3 py-1 text-[11px] font-mono uppercase tracking-wider border ${filter === s ? "bg-ink text-bg border-ink" : "border-line text-ink2 hover:text-ink"}`}>
            {s || "all"}
          </button>
        ))}
      </div>
      {!items && <div className="text-sm text-ink2 font-mono">Loading…</div>}
      {items && visible.length === 0 && <div className="text-sm text-ink2 font-mono p-6 border border-line bg-surface" data-testid="quotes-empty">No {kind === "bulk" ? "bulk" : "single-project"} quote requests {filter && `with status "${filter}"`} yet.</div>}
      {items && visible.length > 0 && (
        <div className="border border-line bg-surface overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left">
                <Th>Ref · Created</Th>
                <Th>Company · Contact</Th>
                {kind === "bulk" ? <Th>Basket · Tons</Th> : <Th>Project · Tons</Th>}
                <Th>Total (USD)</Th>
                <Th>Status</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {visible.map((q) => {
                const isBulk = q.type === "bulk";
                const tonsDisplay = isBulk ? (q.allocation?.total_tons || 0) : (q.tons || 0);
                return (
                <tr key={q.ref} className="border-b border-line/60 align-top" data-testid={`quote-row-${q.ref}`}>
                  <Td>
                    <div className="mono-num text-sm">{q.ref}</div>
                    <div className="text-[10px] font-mono text-ink2 mt-1">{new Date(q.created_at).toLocaleString()}</div>
                    {isBulk && <div className="inline-block mt-1 px-1.5 py-0.5 bg-ink text-white text-[9px] font-mono uppercase tracking-wider">BULK</div>}
                  </Td>
                  <Td>
                    <div className="font-serif">{q.company_name}</div>
                    <div className="text-xs text-ink2 font-mono mt-1">{q.contact_name} · {q.contact_email}</div>
                    <div className="text-xs text-ink2 font-mono">{q.contact_phone}</div>
                  </Td>
                  <Td>
                    {isBulk ? (
                      <>
                        <div className="text-sm">Diversified basket</div>
                        <div className="text-xs text-ink2 mono-num mt-1">
                          {q.allocation?.items?.length || 0} project{(q.allocation?.items?.length || 0) === 1 ? "" : "s"} · {Math.round(tonsDisplay).toLocaleString()} tCO₂ · {q.settlement_method?.toUpperCase()}
                        </div>
                        {q.allocation?.unfulfilled_usd > 0 && (
                          <div className="text-[10px] font-mono text-amber-700 mt-1">
                            ${Number(q.allocation.unfulfilled_usd).toLocaleString()} pending desk reserve
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="text-sm">{q.project_name}</div>
                        <div className="text-xs text-ink2 mono-num mt-1">{Number(tonsDisplay).toLocaleString()} tCO₂ · {q.settlement_method?.toUpperCase()}</div>
                      </>
                    )}
                  </Td>
                  <Td className="mono-num">
                    {fmtMoney(q.total_usd, "USD")}
                    {q.reply && (
                      <div className="text-[10px] font-mono text-ink2 mt-1">
                        replied {new Date(q.reply.sent_at).toLocaleDateString()} · ${Number(q.reply.total_usd).toLocaleString()}
                      </div>
                    )}
                  </Td>
                  <Td><StatusPill status={q.status} /></Td>
                  <Td>
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => setReplyTo(q)}
                        data-testid={`reply-${q.ref}`}
                        className="text-[10px] font-mono uppercase tracking-wider bg-ink text-white px-2 py-1 hover:bg-ink/90 text-left">
                        {q.reply ? "↗ Resend reply" : "↗ Send quote"}
                      </button>
                      {(QUOTE_STATUS_NEXT[q.status] || []).map((nxt) => (
                        <button key={nxt} onClick={() => changeStatus(q.ref, nxt)}
                          className="text-[10px] font-mono uppercase tracking-wider underline underline-offset-4 text-ink2 hover:text-ink text-left">
                          → {nxt}
                        </button>
                      ))}
                    </div>
                  </Td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {replyTo && (
        <QuoteReplyModal
          quote={replyTo}
          onClose={() => setReplyTo(null)}
          onSent={() => { setReplyTo(null); load(); }}
        />
      )}
    </div>
  );
}

function QuoteReplyModal({ quote, onClose, onSent }) {
  const initialPrice = quote.reply?.price_per_ton_usd ?? quote.unit_price_usd ?? 0;
  const [pricePerTon, setPricePerTon] = useState(initialPrice);
  const [totalUsd, setTotalUsd] = useState(quote.reply?.total_usd ?? Number(quote.tons) * initialPrice);
  const [expiry, setExpiry] = useState(quote.reply?.expiry_date ?? "");
  const [rail, setRail] = useState(quote.reply?.payment_rail ?? quote.settlement_method ?? "swift");
  const [notes, setNotes] = useState(quote.reply?.notes ?? "");
  const [busy, setBusy] = useState(false);

  // Auto-compute total when price changes (user can still override).
  useEffect(() => {
    const computed = Number(quote.tons) * Number(pricePerTon || 0);
    if (Number.isFinite(computed)) setTotalUsd(Number(computed.toFixed(2)));
     
  }, [pricePerTon]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/admin/quotes/${quote.ref}/reply`, {
        price_per_ton_usd: Number(pricePerTon),
        total_usd: Number(totalUsd),
        expiry_date: expiry,
        payment_rail: rail,
        notes: notes || null,
      });
      toast.success(`Quote ${quote.ref} sent to ${quote.contact_email}`);
      onSent();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8 bg-ink/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="reply-overlay"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="relative w-full max-w-xl bg-bg border border-ink shadow-2xl max-h-[90vh] overflow-y-auto"
      >
        <div className="border-b border-line px-6 py-4 flex items-center justify-between sticky top-0 bg-bg">
          <div>
            <div className="label-tag">Send structured quote</div>
            <div className="font-serif text-xl mt-1">{quote.ref}</div>
            <div className="text-[11px] font-mono text-ink2 mt-1">
              {quote.company_name} · {quote.contact_email}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-ink2 hover:text-ink text-2xl leading-none px-2"
          >
            ×
          </button>
        </div>

        <div className="px-6 py-5 space-y-4 text-sm">
          <div className="border border-line bg-surface p-3 text-xs text-ink2 font-mono">
            Project: <span className="text-ink">{quote.project_name}</span> ·
            Tons: <span className="text-ink">{Number(quote.tons).toLocaleString()}</span> ·
            Original total: <span className="text-ink">${Number(quote.total_usd).toLocaleString()}</span>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-tag block mb-1">Price / ton (USD)</label>
              <input
                data-testid="reply-price"
                type="number"
                min="0.01"
                step="0.01"
                required
                value={pricePerTon}
                onChange={(e) => setPricePerTon(e.target.value)}
                className="input-field !py-2"
              />
            </div>
            <div>
              <label className="label-tag block mb-1">Total (USD)</label>
              <input
                data-testid="reply-total"
                type="number"
                min="0.01"
                step="0.01"
                required
                value={totalUsd}
                onChange={(e) => setTotalUsd(e.target.value)}
                className="input-field !py-2"
              />
            </div>
            <div>
              <label className="label-tag block mb-1">Valid until</label>
              <input
                data-testid="reply-expiry"
                type="date"
                required
                value={expiry}
                onChange={(e) => setExpiry(e.target.value)}
                className="input-field !py-2"
              />
            </div>
            <div>
              <label className="label-tag block mb-1">Payment rail</label>
              <select
                data-testid="reply-rail"
                value={rail}
                onChange={(e) => setRail(e.target.value)}
                className="input-field !py-2"
              >
                <option value="swift">SWIFT bank wire</option>
                <option value="usdt">USDT (TRC-20)</option>
                <option value="stripe">Stripe (card)</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          <div>
            <label className="label-tag block mb-1">Notes (optional, appears in email)</label>
            <textarea
              data-testid="reply-notes"
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Includes settlement instructions in the attached PDF…"
              className="input-field !py-2"
            />
          </div>

          <div className="text-[11px] font-mono text-ink2 leading-relaxed">
            Sending will email <span className="text-ink">{quote.contact_email}</span> via Resend with the
            structured quote, mark this request as <span className="text-ink">contacted</span>, and store
            the reply in the audit log.
          </div>
        </div>

        <div className="border-t border-line px-6 py-3 flex justify-end gap-2 sticky bottom-0 bg-bg">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-xs font-mono uppercase tracking-wider border border-line text-ink2 hover:text-ink"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            data-testid="reply-send"
            className="btn-primary !text-xs !px-5 !py-2 disabled:opacity-50"
          >
            {busy ? "Sending…" : "Send quote →"}
          </button>
        </div>
      </form>
    </div>
  );
}

// -------- Manual orders (SWIFT / USDT) panel --------
function ManualOrders() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("");

  const load = () => api.get("/admin/manual-orders" + (filter ? `?status=${filter}` : ""))
    .then((r) => setItems(r.data.items || []));
  useEffect(() => { load();   }, [filter]);

  const confirm = async (ref) => {
    if (!window.confirm(`Confirm funds received for ${ref}? This will immediately credit UCS to the buyer.`)) return;
    try {
      const { data } = await api.post(`/admin/manual-orders/${ref}/confirm`);
      toast.success(data.already_credited ? "Already credited." : `Credited ${data.credited_tons} tCO₂.`);
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const cancel = async (ref) => {
    if (!window.confirm(`Cancel pending order ${ref}?`)) return;
    try {
      await api.post(`/admin/manual-orders/${ref}/cancel`);
      toast.success("Order cancelled.");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <div data-testid="orders-panel">
      <div className="flex items-center gap-2 mb-4">
        <span className="label-tag">Filter:</span>
        {["", "pending_payment", "credited", "cancelled"].map((s) => (
          <button key={s || "all"} onClick={() => setFilter(s)}
            className={`px-3 py-1 text-[11px] font-mono uppercase tracking-wider border ${filter === s ? "bg-ink text-bg border-ink" : "border-line text-ink2 hover:text-ink"}`}>
            {s || "all"}
          </button>
        ))}
      </div>
      {!items && <div className="text-sm text-ink2 font-mono">Loading…</div>}
      {items && items.length === 0 && <div className="text-sm text-ink2 font-mono p-6 border border-line bg-surface" data-testid="orders-empty">No manual orders {filter && `with status "${filter}"`} yet.</div>}
      {items && items.length > 0 && (
        <div className="border border-line bg-surface overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left">
                <Th>Ref · Created</Th><Th>Buyer</Th><Th>Project · Tons</Th><Th>Total (USD)</Th><Th>Method</Th><Th>Status</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {items.map((o) => (
                <tr key={o.ref} className="border-b border-line/60 align-top" data-testid={`order-row-${o.ref}`}>
                  <Td>
                    <div className="mono-num text-sm">{o.ref}</div>
                    <div className="text-[10px] font-mono text-ink2 mt-1">{new Date(o.created_at).toLocaleString()}</div>
                  </Td>
                  <Td className="text-xs font-mono">{o.user_email}</Td>
                  <Td>
                    <div className="text-sm">{o.project_name}</div>
                    <div className="text-xs text-ink2 mono-num mt-1">{Number(o.tons).toLocaleString()} tCO₂</div>
                  </Td>
                  <Td className="mono-num">{fmtMoney(o.total_usd, "USD")}</Td>
                  <Td><span className="px-2 py-0.5 text-[10px] font-mono uppercase border border-line">{o.method}</span></Td>
                  <Td><StatusPill status={o.status} /></Td>
                  <Td>
                    {o.status === "pending_payment" && (
                      <div className="flex flex-col gap-1">
                        <button onClick={() => confirm(o.ref)} className="text-[10px] font-mono uppercase tracking-wider bg-leaf text-white px-2 py-1" data-testid={`confirm-${o.ref}`}>
                          Confirm received
                        </button>
                        <button onClick={() => cancel(o.ref)} className="text-[10px] font-mono uppercase tracking-wider text-danger underline underline-offset-4">
                          Cancel
                        </button>
                      </div>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    open: "bg-muted text-ink",
    contacted: "bg-ink text-bg",
    accepted: "bg-leaf text-white",
    declined: "bg-danger text-white",
    expired: "bg-muted text-ink2",
    pending_payment: "bg-amber-500 text-white",
    credited: "bg-leaf text-white",
    cancelled: "bg-muted text-ink2",
  };
  return <span className={`px-2 py-0.5 text-[10px] font-mono uppercase ${map[status] || "bg-muted text-ink2"}`}>{status?.replace("_", " ")}</span>;
}

function Th({ children }) { return <th className="px-4 py-3 text-[11px] font-sans tracking-[0.2em] uppercase text-ink2">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-4 py-3 ${className}`}>{children}</td>; }

// -------- Legal consents audit log --------
function LegalConsents() {
  const [data, setData] = useState(null);
  const [version, setVersion] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (version) params.set("version", version);
      if (q.trim()) params.set("q", q.trim());
      params.set("limit", "500");
      const { data } = await api.get(`/admin/legal-consents?${params.toString()}`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load();   }, []);

  const exportUrl = (ext) => {
    const params = new URLSearchParams();
    if (version) params.set("version", version);
    if (q.trim()) params.set("q", q.trim());
    return `${API}/admin/legal-consents/export.${ext}?${params.toString()}`;
  };

  return (
    <div className="space-y-5" data-testid="consents-panel">
      <div className="flex flex-wrap items-end gap-3 border border-line bg-surface p-4">
        <div className="flex-1 min-w-[200px]">
          <label className="label-tag block mb-1">Search email</label>
          <input
            data-testid="consents-q"
            placeholder="e.g. acme.com"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            className="input-field !py-2"
          />
        </div>
        <div className="min-w-[180px]">
          <label className="label-tag block mb-1">Legal version</label>
          <select
            data-testid="consents-version"
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            className="input-field !py-2"
          >
            <option value="">All versions</option>
            {(data?.available_versions || []).map((v) => (
              <option key={v} value={v}>{v}{v === data?.current_legal_version ? " (current)" : ""}</option>
            ))}
          </select>
        </div>
        <button
          onClick={load}
          data-testid="consents-apply"
          className="btn-primary !text-xs !px-5 !py-3 whitespace-nowrap"
        >
          Apply filter
        </button>
        <a
          href={exportUrl("csv")}
          target="_blank"
          rel="noreferrer"
          data-testid="consents-export-csv"
          className="px-5 py-3 text-xs font-mono uppercase tracking-wider border border-ink text-ink hover:bg-ink hover:text-white transition-colors"
          onClick={() => toast.success("Generating CSV…")}
        >
          Download CSV ↓
        </a>
        <a
          href={exportUrl("pdf")}
          target="_blank"
          rel="noreferrer"
          data-testid="consents-export-pdf"
          className="px-5 py-3 text-xs font-mono uppercase tracking-wider border border-ink text-ink hover:bg-ink hover:text-white transition-colors"
          onClick={() => toast.success("Generating signed PDF…")}
        >
          Download PDF ↓
        </a>
      </div>

      <div className="text-[11px] font-mono text-ink2">
        Current legal version: <span className="text-ink">{data?.current_legal_version || "—"}</span>
        {data && <> · {data.count} record{data.count === 1 ? "" : "s"} shown (limit {data.limit})</>}
      </div>

      {loading && <div className="text-center py-12 text-ink2 font-mono text-sm">Loading…</div>}
      {!loading && data && data.items.length === 0 && (
        <div className="text-center py-12 text-ink2 font-mono text-sm border border-line bg-surface">
          No consent records match the supplied filter.
        </div>
      )}
      {!loading && data && data.items.length > 0 && (
        <div className="border border-line bg-surface overflow-x-auto">
          <table className="w-full text-sm" data-testid="consents-table">
            <thead>
              <tr className="border-b border-line text-left">
                <Th>Accepted at (UTC)</Th>
                <Th>Email</Th>
                <Th>IP</Th>
                <Th>Legal version</Th>
                <Th>Documents</Th>
                <Th>User-Agent</Th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((it, idx) => (
                <tr key={`${it.user_id || it.email}-${idx}`} className="border-b border-line/60 align-top" data-testid={`consent-row-${idx}`}>
                  <Td className="font-mono text-[11px] whitespace-nowrap">{(it.accepted_at || "").replace("T", " ").slice(0, 19)}</Td>
                  <Td className="text-xs">{it.email}</Td>
                  <Td className="font-mono text-[11px]">{it.ip}</Td>
                  <Td className="font-mono text-[11px]">{it.legal_version}</Td>
                  <Td className="text-[11px]">{(it.documents || []).join(", ")}</Td>
                  <Td className="text-[10px] text-ink2 font-mono break-all" style={{ maxWidth: 320 }}>{it.user_agent || ""}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
